<template>
  <div class="payslip-page">
    <div class="payslip-toolbar">
      <button @click="$router.back()">
        <i class="fas fa-arrow-left"></i> Kembali
      </button>
      <div class="payslip-toolbar__actions">
        <template v-if="hasSeparateCommissionSlip">
          <button
            :class="{ active: documentType === 'salary' }"
            @click="documentType = 'salary'"
          >
            <i class="fas fa-file-invoice-dollar"></i> Slip Gaji
          </button>
          <button
            :class="{ active: documentType === 'commission' }"
            @click="documentType = 'commission'"
          >
            <i class="fas fa-receipt"></i> Slip Komisi
          </button>
        </template>
        <button class="print" @click="print">
          <i class="fas fa-print"></i> Cetak
          {{ documentType === "commission" ? "Slip Komisi" : "Slip Gaji" }}
        </button>
      </div>
    </div>
    <article
      v-if="data.employee && documentType === 'salary'"
      class="payslip-sheet"
    >
      <header>
        <div>
          <span>SLIP GAJI</span>
          <h1>{{ (data.clinic && data.clinic.clinic_name) || "Klinik" }}</h1>
          <p>{{ data.clinic && data.clinic.clinic_address }}</p>
        </div>
        <div class="period">
          <small>PERIODE</small><b>{{ data.period.name }}</b
          ><em :class="data.period.status">{{ status(data.period.status) }}</em>
        </div>
      </header>
      <section class="employee-meta">
        <div>
          <small>NIP</small><b>{{ data.employee.employee_number || "-" }}</b>
        </div>
        <div>
          <small>NAMA KARYAWAN</small><b>{{ data.employee.employee_name }}</b>
        </div>
        <div>
          <small>JABATAN</small><b>{{ data.employee.position_name || "-" }}</b>
        </div>
        <div>
          <small>TANGGAL BAYAR</small
          ><b>{{ date(data.period.payment_date) }}</b>
        </div>
      </section>
      <div class="pay-columns">
        <section>
          <h2><i class="fas fa-plus-circle"></i> Pendapatan</h2>
          <PayslipLine
            label="Gaji Pokok"
            :value="data.employee.base_salary"
          /><PayslipLine
            label="Tunjangan"
            :value="data.employee.allowance_amount"
          /><PayslipLine
            label="Lembur"
            :value="data.employee.overtime_amount"
          /><PayslipLine
            :label="commissionLineLabel"
            :value="data.employee.commission_amount"
          /><PayslipLine
            label="THR"
            :value="data.employee.thr_amount"
          /><PayslipLine
            label="Pendapatan Lain"
            :value="data.employee.other_earning_amount"
          />
          <footer>
            <span>Total Pendapatan</span
            ><b>{{ money(data.employee.gross_amount) }}</b>
          </footer>
        </section>
        <section>
          <h2 class="deduction">
            <i class="fas fa-minus-circle"></i> Potongan
          </h2>
          <PayslipLine
            label="BPJS Kesehatan"
            :value="data.employee.bpjs_health_employee"
          /><PayslipLine
            label="JHT Karyawan"
            :value="data.employee.jht_employee"
          /><PayslipLine
            label="JP Karyawan"
            :value="data.employee.jp_employee"
          /><PayslipLine
            label="PPh 21"
            :value="data.employee.pph21_amount"
          /><PayslipLine
            label="Keterlambatan/Absensi"
            :value="data.employee.attendance_deduction"
          /><PayslipLine
            label="Cicilan Kasbon"
            :value="data.employee.loan_deduction"
          /><PayslipLine
            label="Potongan Lain"
            :value="data.employee.other_deduction_amount"
          />
          <footer>
            <span>Total Potongan</span><b>{{ money(totalDeduction) }}</b>
          </footer>
        </section>
      </div>
      <section class="take-home">
        <div>
          <small>TAKE-HOME PAY</small
          ><strong>{{ money(data.employee.net_amount) }}</strong>
          <p>{{ terbilang(data.employee.net_amount) }}</p>
        </div>
        <i class="fas fa-check-circle"></i>
      </section>
      <section class="company-contribution">
        <h2>Kontribusi Klinik (tidak memotong gaji)</h2>
        <div>
          <span
            >BPJS Kesehatan {{ money(data.employee.bpjs_health_company) }}</span
          ><span>JHT {{ money(data.employee.jht_company) }}</span
          ><span>JP {{ money(data.employee.jp_company) }}</span
          ><span>JKK {{ money(data.employee.jkk_company) }}</span
          ><span>JKM {{ money(data.employee.jkm_company) }}</span>
        </div>
      </section>
      <footer class="sheet-footer">
        <p>
          Dokumen dibuat otomatis oleh Medhop. Nilai payroll yang telah dibayar
          dan dikunci tidak dapat diubah tanpa proses koreksi resmi.
        </p>
        <div>
          <span>Penerima,</span><b>{{ data.employee.employee_name }}</b>
        </div>
      </footer>
    </article>
    <article
      v-if="data.employee && documentType === 'commission'"
      class="payslip-sheet commission-payslip"
    >
      <header>
        <div>
          <span>SLIP KOMISI PEGAWAI</span>
          <h1>{{ (data.clinic && data.clinic.clinic_name) || "Klinik" }}</h1>
          <p>{{ data.clinic && data.clinic.clinic_address }}</p>
        </div>
        <div class="period">
          <small>NOMOR SLIP</small><b>{{ data.commission_slip_number }}</b>
          <small class="commission-period-label">PERIODE PAYROLL</small
          ><b>{{ data.period.name }}</b
          ><em :class="data.period.status">{{ status(data.period.status) }}</em>
        </div>
      </header>
      <section class="employee-meta commission-employee-meta">
        <div>
          <small>NIP</small><b>{{ data.employee.employee_number || "-" }}</b>
        </div>
        <div>
          <small>NAMA KARYAWAN</small><b>{{ data.employee.employee_name }}</b>
        </div>
        <div>
          <small>JABATAN</small><b>{{ data.employee.position_name || "-" }}</b>
        </div>
        <div>
          <small>PERIODE</small
          ><b
            >{{ date(data.period.start_date) }} –
            {{ date(data.period.end_date) }}</b
          >
        </div>
      </section>
      <div class="commission-table-wrap">
        <table class="commission-table">
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>Layanan / Tindakan</th>
              <th>Pasien / Referensi</th>
              <th>Dasar Komisi</th>
              <th>Komisi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in data.commission_items" :key="item.id">
              <td>{{ date(item.transaction_date) }}</td>
              <td><b>{{ item.service_name || "Layanan" }}</b></td>
              <td>
                {{ item.patient_name || "-" }}
                <small v-if="item.invoice_number">{{ item.invoice_number }}</small>
              </td>
              <td>{{ commissionBasis(item) }}</td>
              <td class="amount">{{ money(item.amount) }}</td>
            </tr>
            <tr v-if="!data.commission_items || !data.commission_items.length">
              <td colspan="5" class="commission-empty">
                Tidak ada rincian komisi pada periode ini.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <section class="commission-total">
        <div>
          <small>TOTAL KOMISI</small>
          <p>Dibayarkan dan dibukukan melalui payroll {{ data.period.name }}.</p>
        </div>
        <strong>{{ money(data.employee.commission_amount) }}</strong>
      </section>
      <footer class="sheet-footer">
        <p>
          Slip ini merupakan rincian pendukung slip gaji dan bukan pembayaran
          tambahan di luar payroll.
        </p>
        <div>
          <span>Penerima,</span><b>{{ data.employee.employee_name }}</b>
        </div>
      </footer>
    </article>
  </div>
</template>
<script>
import ApiService from "@/core/services/api.service";
const PayslipLine = {
  props: ["label", "value"],
  template: `<div v-if="Number(value||0)!==0" class="pay-line"><span>{{label}}</span><b>{{money(value)}}</b></div>`,
  methods: {
    money(v) {
      return "Rp " + Number(v || 0).toLocaleString("id-ID");
    },
  },
};
export default {
  name: "PayrollPayslip",
  components: { PayslipLine },
  data: () => ({ data: {}, documentType: "salary" }),
  computed: {
    hasSeparateCommissionSlip() {
      return (
        this.data.commission_slip_mode === "separate" &&
        Number((this.data.employee || {}).commission_amount || 0) > 0
      );
    },
    commissionLineLabel() {
      return this.hasSeparateCommissionSlip
        ? "Komisi Layanan (lihat slip komisi)"
        : "Komisi Layanan";
    },
    totalDeduction() {
      const e = this.data.employee || {};
      return (
        Number(e.bpjs_employee_amount || 0) +
        Number(e.pph21_amount || 0) +
        Number(e.attendance_deduction || 0) +
        Number(e.loan_deduction || 0) +
        Number(e.other_deduction_amount || 0)
      );
    },
  },
  async mounted() {
    const r = await ApiService.get(
      `workforce/payroll-periods/${this.$route.params.periodId}/employees/${this.$route.params.lineId}/payslip`
    );
    if (r && r.data) {
      this.data = r.data;
      this.documentType =
        this.$route.query.document === "commission" &&
        this.hasSeparateCommissionSlip
          ? "commission"
          : "salary";
    }
  },
  methods: {
    print() {
      window.print();
    },
    money(v) {
      return "Rp " + Number(v || 0).toLocaleString("id-ID");
    },
    date(v) {
      const value = v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v + "T00:00:00" : v;
      return value
        ? new Date(value).toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "-";
    },
    status(v) {
      return (
        {
          paid: "SUDAH DIBAYAR",
          approved: "DISETUJUI",
          calculated: "DRAFT REVIEW",
        }[v] || String(v).toUpperCase()
      );
    },
    commissionBasis(item) {
      if (item.commission_type === "percentage") {
        const rate = Number(item.commission_value || 0).toLocaleString(
          "id-ID",
          { maximumFractionDigits: 2 }
        );
        return `${rate}% × ${this.money(item.service_price)}`;
      }
      return item.commission_type === "nominal"
        ? "Nominal"
        : "Sesuai layanan";
    },
    terbilang() {
      return "Pembayaran melalui rekening karyawan sesuai data payroll.";
    },
  },
};
</script>
<style lang="scss">
.payslip-page {
  max-width: 900px;
  margin: auto;
}
.payslip-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 15px;
}
.payslip-toolbar__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.payslip-toolbar button {
  border: 1px solid #d8e2ec;
  background: #fff;
  color: #175985;
  padding: 10px 15px;
  border-radius: 9px;
  font-weight: 750;
}
.payslip-toolbar button.active {
  border-color: var(--skin-primary);
  background: #e9f8f5;
  color: #087c76;
}
.payslip-toolbar .print {
  border: 0;
  color: #fff;
  background: linear-gradient(115deg, var(--skin-deep), var(--skin-primary));
}
.payslip-sheet {
  background: #fff;
  border: 1px solid #dbe4ed;
  border-radius: 16px;
  padding: 34px;
  box-shadow: 0 12px 35px rgba(29, 52, 79, 0.08);
  color: #223047;
}
.payslip-sheet > header {
  display: flex;
  justify-content: space-between;
  gap: 30px;
  padding-bottom: 22px;
  border-bottom: 2px solid #0f8e89;
}
.payslip-sheet header span {
  font-size: 11px;
  letter-spacing: 2px;
  color: #0d8e88;
  font-weight: 800;
}
.payslip-sheet h1 {
  font-size: 23px;
  margin: 4px 0;
}
.payslip-sheet p {
  color: #748197;
}
.period {
  text-align: right;
}
.period small,
.period b,
.period em {
  display: block;
}
.period em {
  display: inline-block;
  margin-top: 9px;
  padding: 5px 10px;
  border-radius: 20px;
  background: #e7f7ef;
  color: #128052;
  font-size: 10px;
  font-weight: 850;
  font-style: normal;
}
.period .commission-period-label {
  margin-top: 13px;
}
.employee-meta {
  display: grid;
  grid-template-columns: 1fr 2fr 1.4fr 1fr;
  gap: 15px;
  padding: 20px 0;
}
.employee-meta small {
  display: block;
  color: #7b889a;
  font-size: 10px;
}
.pay-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}
.pay-columns > section {
  border: 1px solid #dfe7ef;
  border-radius: 12px;
  overflow: hidden;
}
.pay-columns h2,
.company-contribution h2 {
  font-size: 13px;
  padding: 12px 15px;
  margin: 0;
  background: var(--skin-soft);
  color: #087c76;
}
.pay-columns h2.deduction {
  background: #fff1f1;
  color: #bd3d48;
}
.pay-line,
.pay-columns footer {
  display: flex;
  justify-content: space-between;
  padding: 9px 15px;
  border-bottom: 1px solid #edf1f5;
  font-size: 12px;
}
.pay-columns footer {
  background: #f5f8fb;
  border: 0;
  font-weight: 800;
}
.take-home {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  padding: 20px;
  border-radius: 13px;
  background: linear-gradient(115deg, var(--skin-deep), var(--skin-primary));
  color: #fff;
}
.take-home small {
  display: block;
  letter-spacing: 1.5px;
}
.take-home strong {
  display: block;
  font-size: 27px;
}
.take-home p {
  color: #d6f2ef;
  margin: 2px 0;
}
.take-home > i {
  font-size: 34px;
}
.company-contribution {
  margin-top: 18px;
  border: 1px solid #e1e8ef;
  border-radius: 12px;
  overflow: hidden;
}
.company-contribution div {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  padding: 13px;
}
.company-contribution span {
  padding: 6px 9px;
  background: #f3f7fa;
  border-radius: 7px;
  font-size: 11px;
}
.sheet-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 40px;
}
.sheet-footer > p {
  font-size: 10px;
  max-width: 55%;
}
.sheet-footer div {
  text-align: center;
  border-top: 1px solid #aebdcb;
  padding: 8px 25px 0;
}
.sheet-footer div span,
.sheet-footer div b {
  display: block;
}
.commission-employee-meta {
  grid-template-columns: 1fr 1.8fr 1.3fr 1.8fr;
}
.commission-table-wrap {
  overflow-x: auto;
  border: 1px solid #dfe7ef;
  border-radius: 12px;
}
.commission-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
}
.commission-table th,
.commission-table td {
  padding: 11px 12px;
  border-bottom: 1px solid #e7edf4;
  vertical-align: top;
  font-size: 12px;
}
.commission-table th {
  background: #f4f7fb;
  color: #53657d;
  font-size: 10px;
  letter-spacing: 0.5px;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}
.commission-table td small {
  display: block;
  margin-top: 2px;
  color: #7a899e;
}
.commission-table th:last-child,
.commission-table td.amount {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.commission-table tbody tr:last-child td {
  border-bottom: 0;
}
.commission-empty {
  padding: 30px !important;
  color: #7a899e;
  text-align: center !important;
}
.commission-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 18px;
  padding: 18px 20px;
  border-radius: 13px;
  background: #e9f8f3;
  color: #08775b;
}
.commission-total small {
  display: block;
  letter-spacing: 1.4px;
  font-weight: 800;
}
.commission-total p {
  margin: 3px 0 0;
  font-size: 11px;
}
.commission-total strong {
  flex: 0 0 auto;
  font-size: 25px;
  font-variant-numeric: tabular-nums;
}
@media (max-width: 650px) {
  .payslip-toolbar,
  .payslip-toolbar__actions {
    align-items: stretch;
    flex-direction: column;
  }
  .payslip-sheet {
    padding: 20px;
  }
  .employee-meta,
  .pay-columns {
    grid-template-columns: 1fr 1fr;
  }
  .payslip-sheet > header {
    flex-direction: column;
  }
  .period {
    text-align: left;
  }
  .commission-total {
    align-items: flex-start;
    flex-direction: column;
  }
}
@media print {
  body {
    background: #fff !important;
  }
  .payslip-toolbar,
  .header,
  .aside,
  .footer {
    display: none !important;
  }
  .payslip-page {
    max-width: none;
  }
  .payslip-sheet {
    box-shadow: none;
    border: 0;
    border-radius: 0;
    padding: 10mm;
  }
  .pay-columns {
    grid-template-columns: 1fr 1fr;
  }
  .commission-table-wrap {
    overflow: visible;
  }
  .commission-table {
    min-width: 0;
  }
}
</style>
